/* eslint-disable react/no-unescaped-entities */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { UserPlus, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const [form, setForm] = useState({ nome: '', cognome: '', email: '', password: '', telefono: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [dataConsent, setDataConsent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyConsent || !dataConsent) {
      setError('Devi acconsentire al trattamento dei dati per procedere');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, form);
      if (res.data.requires_verification) {
        toast.success('Registrazione completata! Controlla la tua email per il codice di verifica.');
        navigate('/verifica-email', { state: { email: form.email } });
      } else {
        // No verification needed (SMTP not configured)
        const user = res.data.user;
        toast.success(`Benvenuto, ${user.nome}! Registrazione completata.`);
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md shadow-[var(--shadow-md)]">
        <CardHeader className="text-center">
          <div className="w-12 h-12 bg-[#238A92] rounded-xl flex items-center justify-center text-white mx-auto mb-2">
            <UserPlus size={24} />
          </div>
          <CardTitle className="text-2xl" style={{fontFamily:'Space Grotesk'}}>Registrati</CardTitle>
          <CardDescription>Crea il tuo account per prenotare appuntamenti</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle size={16} />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome</Label>
                <Input id="nome" name="nome" placeholder="Mario" value={form.nome} onChange={handleChange} required data-testid="register-nome-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cognome">Cognome</Label>
                <Input id="cognome" name="cognome" placeholder="Rossi" value={form.cognome} onChange={handleChange} required data-testid="register-cognome-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" name="email" type="email" placeholder="nome@esempio.com" value={form.email} onChange={handleChange} required data-testid="register-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="telefono">Telefono</Label>
              <Input id="telefono" name="telefono" type="tel" placeholder="+39 333 1234567" value={form.telefono} onChange={handleChange} data-testid="register-telefono-input" />
              <p className="text-xs text-muted-foreground" data-testid="register-telefono-hint">Necessario se si vuole attivare la comunicazione via WhatsApp</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" name="password" type={showPassword ? "text" : "password"} placeholder="Minimo 6 caratteri" value={form.password} onChange={handleChange} required data-testid="register-password-input" className="pr-10" />
                <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#1F2A37] transition-colors">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-3 border-t pt-4">
              <p className="text-xs font-medium text-[#1F2A37]">Consensi obbligatori (Art. 13 GDPR - Reg. UE 679/2016)</p>
              <div className="flex items-start gap-2">
                <Checkbox id="privacy-consent" checked={privacyConsent} onCheckedChange={setPrivacyConsent} className="mt-0.5" />
                <label htmlFor="privacy-consent" className="text-xs text-muted-foreground leading-snug cursor-pointer">
                  Ho letto e compreso l'<Link to="/privacy-policy" className="text-[#238A92] hover:underline" target="_blank">Informativa sulla Privacy</Link> e acconsento al trattamento dei miei dati personali per la creazione dell'account e la gestione degli appuntamenti. *
                </label>
              </div>
              <div className="flex items-start gap-2">
                <Checkbox id="data-consent" checked={dataConsent} onCheckedChange={setDataConsent} className="mt-0.5" />
                <label htmlFor="data-consent" className="text-xs text-muted-foreground leading-snug cursor-pointer">
                  Acconsento al trattamento dei miei dati personali (nome, cognome, email, telefono) da parte dello Studio Medico Boscovich ai fini della prenotazione e gestione degli appuntamenti medici. I dati saranno conservati per il tempo necessario all'erogazione del servizio. *
                </label>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#238A92] hover:bg-[#0F5E66] text-white" disabled={loading || !privacyConsent || !dataConsent} data-testid="register-submit-button">
              {loading ? 'Registrazione in corso...' : 'Registrati'}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Hai gia un account?{' '}
              <Link to="/login" className="text-[#238A92] hover:underline font-medium">Accedi</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
